import React, { useCallback, useEffect, useRef, useState } from 'react';

import css from './SectionListings.module.css';
import { getListings } from '../../../../util/listings';
import { IconArrowHead, ListingCard, NamedLink } from '../../../../components';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { ArrowLinkIcon } from '../MobileHomeIcons';
import { stringify } from '../../../../util/urlHelpers';
import usePolling from '../../../../hooks/usePolling';

const KEY_CODE_ARROW_LEFT = 37;
const KEY_CODE_ARROW_RIGHT = 39;
const LISTINGS_PER_SECTION = 12;
const SCROLL_EDGE_TOLERANCE = 2;
const NEW_ARRIVALS_REFRESH_INTERVAL = 5 * 60 * 1000;

const getVisibleCount = () => {
  if (typeof window === 'undefined') {
    return 5;
  }
  if (window.innerWidth >= 1440) {
    return 6;
  }
  if (window.innerWidth >= 1024) {
    return 5;
  }
  return 2;
};

const SectionListings = props => {
  const { sectionId, startFrom = 0, title, filters, seeAllSearch, moreLabel, autoRefresh } = props;
  const intl = useIntl();

  const [listings, setListings] = useState([]);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const containerRef = useRef(null);
  const sliderRef = useRef(null);
  const requestIdRef = useRef(0);

  const updateArrowVisibility = useCallback(() => {
    const slider = sliderRef.current;
    if (!slider) {
      setCanScrollPrev(false);
      setCanScrollNext(false);
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = slider;
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);

    setCanScrollPrev(scrollLeft > SCROLL_EDGE_TOLERANCE);
    setCanScrollNext(
      maxScrollLeft > SCROLL_EDGE_TOLERANCE && scrollLeft < maxScrollLeft - SCROLL_EDGE_TOLERANCE
    );
  }, []);

  const updateArrowVerticalPosition = useCallback(() => {
    const container = containerRef.current;
    const slider = sliderRef.current;
    if (!container || !slider) {
      return;
    }

    const card = slider.firstElementChild;
    const thumbnail =
      card?.querySelector('[class*="aspectRatioWrapper"]') ||
      card?.querySelector('img')?.parentElement ||
      card?.querySelector('img');

    if (!thumbnail) {
      container.style.removeProperty('--arrowCenterY');
      return;
    }

    const containerTop = container.getBoundingClientRect().top;
    const thumbRect = thumbnail.getBoundingClientRect();
    const centerY = thumbRect.top - containerTop + thumbRect.height / 2;
    container.style.setProperty('--arrowCenterY', `${Math.round(centerY)}px`);
  }, []);

  const loadListings = useCallback(() => {
    const requestId = ++requestIdRef.current;
    return getListings(filters, { throwOnError: true })
      .then(resp => {
        if (requestId === requestIdRef.current && resp.length >= startFrom) {
          setListings(resp.slice(startFrom, startFrom + LISTINGS_PER_SECTION));
        }
      })
      .catch(e => console.log(e));
  }, [filters, startFrom]);

  useEffect(() => {
    loadListings();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadListings]);

  usePolling(loadListings, !!autoRefresh, NEW_ARRIVALS_REFRESH_INTERVAL);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) {
      return undefined;
    }

    const syncArrows = () => {
      updateArrowVisibility();
      updateArrowVerticalPosition();
    };

    syncArrows();

    const onScroll = () => updateArrowVisibility();
    slider.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', syncArrows);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(syncArrows);
      resizeObserver.observe(slider);
    }

    // Images can load after first layout and change thumbnail height.
    const images = slider.querySelectorAll('img');
    images.forEach(img => {
      if (!img.complete) {
        img.addEventListener('load', updateArrowVerticalPosition);
      }
    });

    return () => {
      slider.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', syncArrows);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      images.forEach(img => {
        img.removeEventListener('load', updateArrowVerticalPosition);
      });
    };
  }, [listings, updateArrowVisibility, updateArrowVerticalPosition]);

  const onSlideLeft = e => {
    const slider = sliderRef.current;
    const card = slider?.firstChild;
    if (!slider || !card) {
      return;
    }
    const gap = parseFloat(window.getComputedStyle(slider).gap) || 0;
    const slideWidth = getVisibleCount() * (card.clientWidth + gap);
    const nextLeft = Math.max(0, slider.scrollLeft - slideWidth);
    slider.scrollTo({ left: nextLeft, behavior: 'smooth' });
    e.currentTarget.focus();
  };

  const onSlideRight = e => {
    const slider = sliderRef.current;
    const card = slider?.firstChild;
    if (!slider || !card) {
      return;
    }
    const gap = parseFloat(window.getComputedStyle(slider).gap) || 0;
    const slideWidth = getVisibleCount() * (card.clientWidth + gap);
    const maxScrollLeft = Math.max(0, slider.scrollWidth - slider.clientWidth);
    const remaining = maxScrollLeft - slider.scrollLeft;
    const nextLeft =
      remaining <= slideWidth + SCROLL_EDGE_TOLERANCE
        ? maxScrollLeft
        : Math.min(maxScrollLeft, slider.scrollLeft + slideWidth);
    slider.scrollTo({ left: nextLeft, behavior: 'smooth' });
    e.currentTarget.focus();
  };

  const onKeyDown = e => {
    if (e.keyCode === KEY_CODE_ARROW_LEFT) {
      e.preventDefault();
      onSlideLeft(e);
    } else if (e.keyCode === KEY_CODE_ARROW_RIGHT) {
      e.preventDefault();
      onSlideRight(e);
    }
  };

  const listingCount = listings.length;
  const seeAllTo =
    seeAllSearch == null
      ? null
      : { search: Object.keys(seeAllSearch).length ? `?${stringify(seeAllSearch)}` : '' };

  return (
    <div id={sectionId} className={css.sectionContent} style={{ backgroundColor: 'white' }}>
      <div className={css.secHead}>
        {title && <h4 className={css.desktopTitle}>{title}</h4>}
        {seeAllTo ? (
          <NamedLink className={css.seeAll} name="SearchPage" to={seeAllTo}>
            <FormattedMessage id="LandingPage.mobileHome.listings.seeAll" />
            <ArrowLinkIcon size={13} strokeWidth={2.6} />
          </NamedLink>
        ) : null}
      </div>
      <div className={css.carouselContainer} ref={containerRef}>
        {listingCount > 0 ? (
          <>
            <div className={css.carouselArrows}>
              {canScrollPrev ? (
                <button
                  type="button"
                  className={css.carouselArrowPrev}
                  onClick={onSlideLeft}
                  onKeyDown={onKeyDown}
                  aria-label={intl.formatMessage({
                    id: 'LandingPage.mobileHome.listings.prevListings',
                  })}
                >
                  <IconArrowHead direction="left" size="big" rootClassName={css.carouselArrowSvg} />
                </button>
              ) : null}
              {canScrollNext ? (
                <button
                  type="button"
                  className={css.carouselArrowNext}
                  onClick={onSlideRight}
                  onKeyDown={onKeyDown}
                  aria-label={intl.formatMessage({
                    id: 'LandingPage.mobileHome.listings.nextListings',
                  })}
                >
                  <IconArrowHead
                    direction="right"
                    size="big"
                    rootClassName={css.carouselArrowSvg}
                  />
                </button>
              ) : null}
            </div>
            <div className={css.carouselTrack} ref={sliderRef}>
              {listings.map(l => (
                <ListingCard
                  className={css.listingCard}
                  key={l.id.uuid}
                  listing={l}
                  listingExpanded={l}
                  setActiveListing={() => {}}
                />
              ))}
              {seeAllTo && moreLabel ? (
                <NamedLink className={css.moreCard} name="SearchPage" to={seeAllTo}>
                  <span className={css.moreCir}>
                    <ArrowLinkIcon size={16} strokeWidth={2.4} />
                  </span>
                  {moreLabel}
                </NamedLink>
              ) : null}
            </div>
          </>
        ) : (
          <p className={css.noListings}>
            <FormattedMessage id="LandingPage.mobileHome.listings.noListings" />
          </p>
        )}
      </div>
    </div>
  );
};

export default SectionListings;
